import { Button, Caption, Heading, Paragraph, Stack } from '@mediahuis/chameleon-react';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import RegistrationCompletionForm from '../../components/RegistrationCompletionForm';
import { useBrandGroupConfig } from '../../hooks/useBrandGroupConfig';
import { useQueryParams } from '../../hooks/useQueryParams';
import { useTracking } from '../../hooks/useTracking';
import { useAuthenticationContext } from '../../providers/AuthenticationProvider';
import { useAppDispatch } from '../../redux/hooks';
import { setContext } from '../../redux/reducers/contextReducer';
import {
  EventActionValues,
  EventCategoryValues,
  EventLabelValues,
  VIEWS,
} from '../../types/tracking';
import { authContinue } from '../../utils/authUtils';

function RegistrationCompletion() {
  const { t } = useTranslation();
  const { user } = useAuthenticationContext();
  const brandGroupConfigHook = useBrandGroupConfig();
  const { brand } = useQueryParams();
  const brandConfig = brandGroupConfigHook.getBrandConfigFromBrandId(brand);
  const dispatch = useAppDispatch();
  const { trackEvent } = useTracking();

  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

  useEffect(() => {
    // On page load and brand/brandGroup present
    dispatch(
      setContext({ eventCategory: EventCategoryValues.Registration, view: VIEWS.PERSONAL_INFO })
    );
    if (brand) trackEvent(EventActionValues.Show, EventLabelValues.PersonalInfo);
  }, [brand]);

  useEffect(() => {
    if (hasSubmitted) {
      dispatch(
        setContext({ eventCategory: EventCategoryValues.Registration, view: VIEWS.CONFIRM_EMAIL })
      );
      trackEvent(EventActionValues.Show, EventLabelValues.ConfirmEmail);
    }
  }, [hasSubmitted]);

  const handleContinue = async () => {
    trackEvent(EventActionValues.Click, EventLabelValues.ConfirmEmailContinue);
    await window.auth.clearUser();
    authContinue();
  };

  return (
    <>
      <Stack alignItems="center" flexDirection="column" className="m-auto px-6">
        <Heading
          as="h1"
          size="sm"
          className="text-center mt-6 mb-3"
          id="container__registrationFlowHeaderText">
          {hasSubmitted
            ? `${t('register.finished.title')}`
            : t('register.disclaimers.title', { email: user?.email })}
        </Heading>
        {hasSubmitted && (
          <>
            <Caption size="lg" className="paper__text--light" data-cy="validateEmailHeader">
              <Trans
                i18nKey="register.finished.validateYourEmail"
                components={{ strong: <strong /> }}
                values={{ email: user?.email }}
              />
            </Caption>
            <Button
              type="submit"
              id="registrationFinished"
              size="lg"
              width="full"
              data-cy="validateEmailButton"
              appearance="primary"
              onClick={handleContinue}>
              <Stack alignItems="center">
                <Trans
                  i18nKey="register.finished.buttonText"
                  values={{ url: brandConfig?.urlDisplay }}
                />
              </Stack>
            </Button>
          </>
        )}
        {!hasSubmitted && (
          <Caption size="lg" className="paper__text--light">
            {t('register.disclaimers.subtext')}
          </Caption>
        )}
      </Stack>
      {!hasSubmitted && (
        <RegistrationCompletionForm
          whenSubmitted={() => setHasSubmitted(true)}
          email={user?.email}
        />
      )}
    </>
  );
}

export default RegistrationCompletion;
